import React, { useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import DatePicker from 'react-datepicker';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

// Registrar el locale español
registerLocale('es', es);
setDefaultLocale('es');
// import { lectureService } from '@/services/api/services/lectureService'; // Removido - no se usa
import { REDIS_API_URL } from '@/services/api/config';
import { datePickerValueAsTimezoneToUtcEpochSeconds } from '@/utils/lectureTimeUtils';
import { toast } from 'react-toastify';
import { CreateLectureData, LectureEventType } from '@/types';
import { useEventTypes } from '@/hooks/useEventTypes';
import { Package, Braces, Layers, Download, AlertTriangle, icons, LucideIcon } from 'lucide-react';

// Coordinates for different cities
const CITY_COORDINATES = {
  'bs-as': { latitude: -34.6037, longitude: -58.3816, name: 'Buenos Aires' },
  'la-plata': { latitude: -34.9214, longitude: -57.9544, name: 'La Plata' },
  zarate: { latitude: -34.0981, longitude: -59.0287, name: 'Zárate' },
  pergamino: { latitude: -33.8906, longitude: -60.5736, name: 'Pergamino' },
  'rio-tercero': { latitude: -32.1731, longitude: -64.1142, name: 'Río Tercero' },
  'sur-cordoba': { latitude: -31.4201, longitude: -64.1888, name: 'Sur de Córdoba' },
  'san-juan': { latitude: -31.5375, longitude: -68.5364, name: 'San Juan' },
  'mina-pachon': { latitude: -31.75371, longitude: -70.43057, name: 'Mina El Pachón' },
  gualeguaychu: { latitude: -33.0104, longitude: -58.5142, name: 'Gualeguaychú' },
  'concepcion-uruguay': { latitude: -32.4849, longitude: -58.2322, name: 'Concepción del Uruguay' },
  colon: { latitude: -32.2231, longitude: -58.1444, name: 'Colón' },
  parana: { latitude: -31.7444, longitude: -60.5175, name: 'Paraná' },
};

const DEFAULT_ZOOM = 6;

/** Opciones de zona horaria para la fecha/hora base de las lecturas (bulk insert) */
const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
  { value: 'America/Montevideo', label: 'Uruguay (Montevideo)' },
  { value: 'America/Santiago', label: 'Chile (Santiago)' },
  { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo)' },
  { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
  { value: 'America/New_York', label: 'Estados Unidos (Nueva York)' },
  { value: 'America/Los_Angeles', label: 'Estados Unidos (Los Ángeles)' },
  { value: 'Europe/Madrid', label: 'España (Madrid)' },
];

const LEGACY_EVENT_ORDER = ['SUB', 'PAR', 'BAJ', 'CAL', 'INC', 'DES', 'CFE', 'CHO', 'MMA', 'CAN'];
const LOCKED_EVENT_CODES = ['SUB', 'BAJ'];

const hexToRgba = (hex: string, alpha: number) => {
  const sanitized = hex.replace('#', '');
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((char) => char + char)
          .join('')
      : sanitized;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface BulkLectureFormData {
  devId: string;
  opId?: string;
  ts: number;
  tz?: string;
  coords: {
    lat: number;
    long: number;
  };
  type: string;
  ang: {
    lat: number;
    long: number;
  };
  lim: {
    lat: number;
    long: number;
  };
  seq: number;
  order?: number;
}

interface BulkInsertResult {
  success: boolean;
  data?: unknown;
  error?: unknown;
  index: number;
}

export const BulkLectureInsertPage: React.FC = () => {
  const { eventTypes, loading: loadingEventTypes } = useEventTypes();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [insertResults, setInsertResults] = useState<BulkInsertResult[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [previewLectures, setPreviewLectures] = useState<CreateLectureData[]>([]);
  const [lastInsertedLectures, setLastInsertedLectures] = useState<CreateLectureData[]>([]);
  const [lastInsertedHadAnomalies, setLastInsertedHadAnomalies] = useState(false);

  // Form data for bulk insertion
  const [customEventCodeInput, setCustomEventCodeInput] = useState('');
  const [customEventCodes, setCustomEventCodes] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    skey: '',
    opId: undefined as string | undefined,
    numberOfLectures: 10,
    centerCoords: {
      lat: CITY_COORDINATES['zarate'].latitude,
      long: CITY_COORDINATES['zarate'].longitude,
    },
    selectedCity: 'zarate',
    variationMeters: 100,
    selectedTypes: LOCKED_EVENT_CODES as string[],
    angleRanges: {
      lat: { min: -5, max: 5 },
      long: { min: -5, max: 5 },
    },
    limitValues: {
      lat: 5, // Valor para límites laterales (se aplicará como -valor/+valor)
      long: 5, // Valor para límites longitudinales (se aplicará como -valor/+valor)
    },
    startSequence: 1,
    delayMs: 100,
    tz: 'America/Argentina/Buenos_Aires',
    timeSpacing: {
      type: 'seconds' as 'seconds' | 'minutes',
      value: 60,
    },
    // Nuevos ajustes
    autoIncrementSequence: true,
    timeAdjustmentMode: 'move_to_end' as 'keep_same' | 'move_to_end' | 'move_to_end_plus_31',
    // Variación de posición
    varyPosition: false,
    positionVariationMode: 'no' as 'no' | 'vary_10km' | 'vary_cities',
    // Testing de anomalías de secuencia
    generateAnomalies: false,
    anomalyConfig: {
      gapProbability: 0.1, // 10% chance de gap
      negativeProbability: 0.05, // 5% chance de secuencia negativa
      duplicateProbability: 0.02, // 2% chance de duplicado
      maxGapSize: 50, // Máximo gap de 50 secuencias
    },
    // Testing de anomalías de fecha
    enableDateAnomalies: false, // Toggle para anomalías de fecha
    futureDateProbability: 0, // 0% chance de fecha en el futuro (2400)
    pastDateProbability: 0, // 0% chance de fecha en el pasado (1950)
  });

  const eventTypeOptions = useMemo(() => {
    const sorted = [...eventTypes].sort((a, b) => {
      const ai = LEGACY_EVENT_ORDER.indexOf(a.code);
      const bi = LEGACY_EVENT_ORDER.indexOf(b.code);
      if (ai === -1 && bi === -1) return a.code.localeCompare(b.code);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    const catalogOptions = sorted.map((item) => {
      const Icon =
        (icons[(item.icon || 'CircleUserRound') as keyof typeof icons] as LucideIcon) ||
        icons.CircleUserRound;
      return {
        value: item.code,
        label: item.labelEs,
        active: item.active,
        custom: false,
        icon: <Icon className="w-4 h-4" style={{ color: item.iconColor }} />,
        styles: {
          bg: hexToRgba(item.bgColor || '#e5e7eb', 0.55),
          border: hexToRgba(item.bgColor || '#e5e7eb', 0.95),
          text: item.textColor || '#374151',
          selectedBg: item.bgColor || '#e5e7eb',
          selectedBorder: hexToRgba(item.bgColor || '#e5e7eb', 0.95),
          selectedText: item.textColor || '#374151',
        },
      };
    });

    const catalogCodes = new Set(catalogOptions.map((o) => o.value));
    const AlertIcon = icons.TriangleAlert;
    const customOptions = customEventCodes
      .filter((code) => !catalogCodes.has(code))
      .map((code) => ({
        value: code,
        label: `${code} (nuevo)`,
        active: true,
        custom: true,
        icon: <AlertIcon className="w-4 h-4" style={{ color: '#6b7280' }} />,
        styles: {
          bg: hexToRgba('#e5e7eb', 0.55),
          border: hexToRgba('#e5e7eb', 0.95),
          text: '#6b7280',
          selectedBg: '#e5e7eb',
          selectedBorder: hexToRgba('#9ca3af', 0.95),
          selectedText: '#374151',
        },
      }));

    return [...catalogOptions, ...customOptions];
  }, [eventTypes, customEventCodes]);

  const eventTypesMap = useMemo(() => {
    return new Map(eventTypeOptions.map((item) => [item.value, item]));
  }, [eventTypeOptions]);

  React.useEffect(() => {
    if (eventTypeOptions.length === 0 && customEventCodes.length === 0) return;
    const availableCodes = new Set(eventTypeOptions.map((item) => item.value));
    setFormData((prev) => {
      const filtered = prev.selectedTypes.filter(
        (code) => availableCodes.has(code) || customEventCodes.includes(code)
      );
      const locked = LOCKED_EVENT_CODES.filter((code) => availableCodes.has(code));
      const merged = Array.from(new Set([...locked, ...filtered]));
      if (
        merged.length === prev.selectedTypes.length &&
        merged.every((code, index) => code === prev.selectedTypes[index])
      ) {
        return prev;
      }
      return { ...prev, selectedTypes: merged };
    });
  }, [eventTypeOptions, customEventCodes]);

  React.useEffect(() => {
    const map = new mapboxgl.Map({
      container: 'map-container',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [formData.centerCoords.long, formData.centerCoords.lat],
      zoom: DEFAULT_ZOOM,
    });

    const marker = new mapboxgl.Marker({
      draggable: true,
    })
      .setLngLat([formData.centerCoords.long, formData.centerCoords.lat])
      .addTo(map);

    map.on('click', (e) => {
      const { lng, lat } = e.lngLat;
      marker.setLngLat([lng, lat]);
      setFormData((prev) => ({
        ...prev,
        centerCoords: {
          lat: lat,
          long: lng,
        },
      }));
    });

    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      setFormData((prev) => ({
        ...prev,
        centerCoords: {
          lat: lat,
          long: lng,
        },
      }));
    });

    return () => map.remove();
  }, [formData.centerCoords.lat, formData.centerCoords.long]);

  const adjustTime = (minutes: number) => {
    const newDate = new Date(selectedDate.getTime() + minutes * 60000);
    setSelectedDate(newDate);
  };

  const setToday = () => {
    const today = new Date();
    setSelectedDate(today);
  };

  const setNextDay = () => {
    const nextDay = new Date(selectedDate.getTime() + 24 * 60 * 60 * 1000);
    setSelectedDate(nextDay);
  };

  // Calculate initial and final lecture dates
  const calculateLectureDates = () => {
    const timeIncrement =
      formData.timeSpacing.type === 'minutes'
        ? formData.timeSpacing.value * 60
        : formData.timeSpacing.value;

    const initialDate = new Date(selectedDate);
    const finalDate = new Date(
      selectedDate.getTime() + (formData.numberOfLectures - 1) * timeIncrement * 1000
    );

    return {
      initial: initialDate,
      final: finalDate,
    };
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  const toggleEventType = (type: string) => {
    // No permitir deseleccionar SUB y BAJ
    if ((type === 'SUB' || type === 'BAJ') && formData.selectedTypes.includes(type)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedTypes: prev.selectedTypes.includes(type)
        ? prev.selectedTypes.filter((t) => t !== type)
        : [...prev.selectedTypes, type],
    }));
  };

  const addCustomEventCode = () => {
    const code = customEventCodeInput.toUpperCase().trim();
    if (!/^[A-Z0-9]{3}$/.test(code)) {
      toast.error('El código debe ser exactamente 3 caracteres alfanuméricos');
      return;
    }
    setCustomEventCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
    setFormData((prev) => ({
      ...prev,
      selectedTypes: prev.selectedTypes.includes(code)
        ? prev.selectedTypes
        : [...prev.selectedTypes, code],
    }));
    setCustomEventCodeInput('');
    toast.success(`Tipo ${code} agregado para testing (se auto-creará al enviar si no existe)`);
  };

  const removeCustomEventCode = (code: string) => {
    setCustomEventCodes((prev) => prev.filter((c) => c !== code));
    setFormData((prev) => ({
      ...prev,
      selectedTypes: prev.selectedTypes.filter((t) => t !== code),
    }));
  };

  const toggleAllEventTypes = () => {
    const allTypes = eventTypeOptions.map((t) => t.value);
    const hasAllSelected = allTypes.every((type) => formData.selectedTypes.includes(type));

    if (hasAllSelected) {
      // Deseleccionar todo excepto SUB y BAJ
      setFormData((prev) => ({
        ...prev,
        selectedTypes: LOCKED_EVENT_CODES,
      }));
    } else {
      // Seleccionar todo (catálogo + custom)
      setFormData((prev) => ({
        ...prev,
        selectedTypes: allTypes,
      }));
    }
  };

  const handleCityChange = (cityKey: string) => {
    const city = CITY_COORDINATES[cityKey as keyof typeof CITY_COORDINATES];
    setFormData((prev) => ({
      ...prev,
      selectedCity: cityKey,
      centerCoords: {
        lat: city.latitude,
        long: city.longitude,
      },
    }));
  };

  // Generate random coordinates within variation
  const generateRandomCoords = () => {
    const latVariation = formData.variationMeters / 111000; // Approximate meters to degrees
    const longVariation =
      formData.variationMeters / (111000 * Math.cos((formData.centerCoords.lat * Math.PI) / 180));

    const lat = formData.centerCoords.lat + (Math.random() - 0.5) * 2 * latVariation;
    const long = formData.centerCoords.long + (Math.random() - 0.5) * 2 * longVariation;

    return {
      lat: Math.round(lat * 1000000) / 1000000, // Round to 6 decimal places
      long: Math.round(long * 1000000) / 1000000, // Round to 6 decimal places
    };
  };

  // Generate random angle within range
  const generateRandomAngle = (range: { min: number; max: number }) => {
    const value = Math.random() * (range.max - range.min) + range.min;
    return Math.round(value * 10) / 10; // Round to 1 decimal place
  };

  // Generate sequences with anomalies for testing
  const generateSequencesWithAnomalies = (count: number, startSeq: number): number[] => {
    const sequences: number[] = [];
    let currentSeq = startSeq;

    for (let i = 0; i < count; i++) {
      const random = Math.random();

      // Generar secuencia negativa
      if (random < formData.anomalyConfig.negativeProbability) {
        sequences.push(-Math.floor(Math.random() * 10) - 1); // -1 a -10
        currentSeq = startSeq + i + 1; // Continuar desde donde debería estar
        continue;
      }

      // Generar gap
      if (
        random <
        formData.anomalyConfig.negativeProbability + formData.anomalyConfig.gapProbability
      ) {
        const gapSize = Math.floor(Math.random() * formData.anomalyConfig.maxGapSize) + 1;
        currentSeq += gapSize; // Saltar secuencias
      }

      // Generar duplicado
      if (
        random <
        formData.anomalyConfig.negativeProbability +
          formData.anomalyConfig.gapProbability +
          formData.anomalyConfig.duplicateProbability
      ) {
        // Usar la secuencia anterior si existe, sino la actual
        const duplicateSeq = sequences.length > 0 ? sequences[sequences.length - 1] : currentSeq;
        sequences.push(duplicateSeq);
        continue;
      }

      // Secuencia normal
      sequences.push(currentSeq);
      currentSeq++;
    }

    return sequences;
  };

  // Generate event type based on distribution rules
  const generateEventType = (index: number, total: number) => {
    const hasSub = formData.selectedTypes.includes('SUB');
    const hasBaj = formData.selectedTypes.includes('BAJ');
    const hasBothSubBaj = hasSub && hasBaj;

    const random = Math.random();
    const isFirstHalf = index < total / 2;

    if (hasBothSubBaj) {
      // Caso "lindo": SUB y BAJ están seleccionados
      if (random < 0.7) {
        // 70% chance of SUB/BAJ distribution
        if (isFirstHalf) {
          return 'SUB'; // First half: more SUB
        } else {
          return 'BAJ'; // Second half: more BAJ
        }
      } else {
        // 30% chance of other types
        const otherTypes = formData.selectedTypes.filter((t) => t !== 'SUB' && t !== 'BAJ');
        if (otherTypes.length === 0) {
          return hasSub ? 'SUB' : hasBaj ? 'BAJ' : formData.selectedTypes[0];
        }
        return otherTypes[Math.floor(Math.random() * otherTypes.length)];
      }
    } else if (hasSub && !hasBaj) {
      // Solo SUB está seleccionado
      if (random < 0.5) {
        return 'SUB';
      } else {
        const otherTypes = formData.selectedTypes.filter((t) => t !== 'SUB');
        return otherTypes[Math.floor(Math.random() * otherTypes.length)];
      }
    } else if (!hasSub && hasBaj) {
      // Solo BAJ está seleccionado
      if (random < 0.5) {
        return 'BAJ';
      } else {
        const otherTypes = formData.selectedTypes.filter((t) => t !== 'BAJ');
        return otherTypes[Math.floor(Math.random() * otherTypes.length)];
      }
    } else {
      // Ni SUB ni BAJ están seleccionados - completamente aleatorio
      return formData.selectedTypes[Math.floor(Math.random() * formData.selectedTypes.length)];
    }
  };

  const generateBulkLectures = () => {
    const lectures: CreateLectureData[] = [];
    // Interpretar la fecha/hora del picker como hora en la TZ seleccionada (ej. Argentina),
    // no en la hora del navegador (ej. España), para que 12:09 sea 12:09 en esa TZ.
    const baseTimestamp = datePickerValueAsTimezoneToUtcEpochSeconds(
      selectedDate,
      formData.tz ?? 'America/Argentina/Buenos_Aires'
    );

    // Calculate time increment based on spacing type
    const timeIncrement =
      formData.timeSpacing.type === 'minutes'
        ? formData.timeSpacing.value * 60
        : formData.timeSpacing.value;

    // Generar secuencias con anomalías si está habilitado
    const sequences = formData.generateAnomalies
      ? generateSequencesWithAnomalies(formData.numberOfLectures, formData.startSequence)
      : Array.from({ length: formData.numberOfLectures }, (_, i) => formData.startSequence + i);

    for (let i = 0; i < formData.numberOfLectures; i++) {
      const coords = generateRandomCoords();
      const eventType = generateEventType(i, formData.numberOfLectures);

      // Calcular timestamp base
      let lectureTimestamp = baseTimestamp + i * timeIncrement;

      // Aplicar anomalías de fecha si está configurado
      if (formData.enableDateAnomalies) {
        const random = Math.random();

        // Primero verificar si debe ser fecha en el pasado (≤1960)
        if (formData.pastDateProbability > 0 && random < formData.pastDateProbability) {
          // Fecha en 1950 (timestamp: -631152000)
          lectureTimestamp = -631152000;
        }
        // Luego verificar si debe ser fecha en el futuro (≥2300)
        else if (formData.futureDateProbability > 0 && random < formData.futureDateProbability) {
          // Fecha en 2400 (timestamp: 16725312000)
          lectureTimestamp = 16725312000;
        }
      }

      const lecture: CreateLectureData = {
        skey: formData.skey,
        opId: formData.opId,
        ts: lectureTimestamp.toString(),
        tz: formData.tz,
        coords: {
          lat: coords.lat,
          long: coords.long,
        },
        type: eventType as LectureEventType,
        ang: {
          lat: generateRandomAngle(formData.angleRanges.lat),
          long: generateRandomAngle(formData.angleRanges.long),
        },
        lim: {
          lat: formData.limitValues.lat,
          long: formData.limitValues.long,
        },
        seq: sequences[i],
      };

      lectures.push(lecture);
    }

    return lectures;
  };

  // Función para actualizar secuencia y fecha después de ejecución exitosa
  const updateFormAfterSuccess = (lectureCount: number) => {
    if (formData.autoIncrementSequence) {
      setFormData((prev) => ({
        ...prev,
        startSequence: prev.startSequence + lectureCount,
      }));
    }

    if (formData.timeAdjustmentMode !== 'keep_same') {
      const timeIncrement =
        formData.timeSpacing.type === 'minutes'
          ? formData.timeSpacing.value * 60
          : formData.timeSpacing.value;

      let minutesToAdd = 0;

      if (formData.timeAdjustmentMode === 'move_to_end') {
        // Mover al final de las lecturas enviadas
        minutesToAdd = ((lectureCount - 1) * timeIncrement) / 60; // Convertir a minutos
      } else if (formData.timeAdjustmentMode === 'move_to_end_plus_31') {
        // Mover al final + 31 minutos
        minutesToAdd = ((lectureCount - 1) * timeIncrement) / 60 + 31;
      }

      setSelectedDate((prevDate) => {
        const newDate = new Date(prevDate.getTime() + minutesToAdd * 60000);
        return newDate;
      });
    }

    // Actualizar posición si está habilitada la variación post-ejecución
    if (formData.positionVariationMode !== 'no') {
      if (formData.positionVariationMode === 'vary_cities') {
        // Seleccionar una ciudad aleatoria
        const cityKeys = Object.keys(CITY_COORDINATES);
        const randomCityKey = cityKeys[Math.floor(Math.random() * cityKeys.length)];
        handleCityChange(randomCityKey);
      } else if (formData.positionVariationMode === 'vary_10km') {
        // Variar la posición actual +/- 10km (evitando el mar)
        const latVariation = 10 / 111; // Aproximadamente 10km en grados
        const longVariation = 10 / (111 * Math.cos((formData.centerCoords.lat * Math.PI) / 180));

        let newLat = formData.centerCoords.lat + (Math.random() - 0.5) * 2 * latVariation;
        let newLong = formData.centerCoords.long + (Math.random() - 0.5) * 2 * longVariation;

        // Protección básica contra caer en el mar (Argentina)
        // Límites aproximados de Argentina continental
        newLat = Math.max(-55, Math.min(-21, newLat));
        newLong = Math.max(-73, Math.min(-53, newLong));

        setFormData((prev) => ({
          ...prev,
          centerCoords: {
            lat: newLat,
            long: newLong,
          },
        }));
      }
    }
  };

  const handleGeneratePreview = () => {
    if (formData.selectedTypes.length === 0) {
      setFormError('Debe seleccionar al menos un tipo de evento');
      return;
    }

    setFormError(null);
    const lectures = generateBulkLectures();
    setPreviewLectures(lectures);
    toast.success(`Vista previa generada: ${lectures.length} lecturas`);
  };

  const handleDownloadPreviewJson = () => {
    if (previewLectures.length === 0) {
      setFormError('No hay vista previa para descargar');
      return;
    }

    const fileContent = JSON.stringify(previewLectures, null, 2);
    const blob = new window.Blob([fileContent], { type: 'application/json;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    anchor.href = url;
    anchor.download = `lectures-preview-${previewLectures.length}-${timestamp}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

    toast.success('JSON descargado. Ya podés usarlo en la pantalla de importar');
  };

  // Función removida - handleSendPreview (método tradicional)

  // Función removida - handleSendBatchPreview (método batch)

  const handleSendBatchWithQueue = async () => {
    if (previewLectures.length === 0) {
      setFormError('No hay vista previa para enviar. Genera una vista previa primero.');
      return;
    }

    setFormError(null);
    setInsertResults([]);
    setIsLoading(true);
    setProgress({ current: 0, total: 1 }); // Solo 1 operación para el batch

    try {
      console.log('🚀 Enviando lote de lecturas al nuevo servicio con colas:', {
        count: previewLectures.length,
        device: formData.skey,
      });

      // Preparar datos para el nuevo endpoint device-batch
      const deviceBatchData = {
        deviceId: formData.skey,
        lectures: previewLectures.map((lecture) => ({
          skey: lecture.skey,
          ts: lecture.ts,
          tz: lecture.tz,
          coords: lecture.coords,
          type: lecture.type,
          ang: lecture.ang,
          lim: lecture.lim,
          seq: lecture.seq,
          order: lecture.order,
        })),
      };

      // Llamar al nuevo endpoint device-batch en el lecture-queue-service
      const response = await window.fetch(`${REDIS_API_URL}/api/v1/device-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deviceBatchData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          `✅ Lote de ${previewLectures.length} lecturas enviado exitosamente al sistema de colas`
        );

        // Mostrar resultado exitoso en la UI
        setInsertResults([
          {
            success: true,
            data: {
              batchId: result.batchId,
              message: result.message,
              count: result.lectureCount,
              estimatedProcessingTime: result.estimatedProcessingTime,
            },
            index: 1,
          },
        ]);

        // Guardar los datos de la última inserción antes de limpiar
        setLastInsertedLectures([...previewLectures]);
        setLastInsertedHadAnomalies(formData.generateAnomalies);

        setPreviewLectures([]); // Clear preview after successful send
        // Actualizar secuencia y fecha después del éxito
        updateFormAfterSuccess(previewLectures.length);
      } else {
        throw new Error(result.message || 'Error en el servicio de colas');
      }
    } catch (error) {
      console.error('Error en inserción con colas:', error);
      toast.error(
        `❌ Error en inserción con colas: ${error instanceof Error ? error.message : 'Error desconocido'}`
      );
      setFormError(
        `Error en inserción con colas: ${error instanceof Error ? error.message : 'Error desconocido'}`
      );

      setInsertResults([
        {
          success: false,
          error: error,
          index: 1,
        },
      ]);
    } finally {
      setIsLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2">
        <Package className="w-8 h-8 text-brand-orange" />
        Inserción masiva, por lotes, de lecturas
      </h1>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-orange"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
              Insertando Lecturas
            </h3>
            <p className="text-sm text-gray-600 text-center mb-2">
              {progress.current} de {progress.total} lecturas procesadas
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-orange h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            {/* ZONA 1: Controles Básicos */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-900 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Zona 1: Controles básicos
              </h3>
              <div className="space-y-4">
                {/* Serial Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Número de serie</label>
                  <input
                    type="text"
                    value={formData.skey}
                    onChange={(e) => setFormData((prev) => ({ ...prev, skey: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Identificador único del dispositivo</p>
                </div>

                {/* Number of Lectures */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Cantidad de lecturas (1-1000)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.numberOfLectures}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        numberOfLectures: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Número total de lecturas a generar</p>
                </div>

                {/* Sequence - Moved below lectures */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Secuencia inicial (permite -1 para testing)
                  </label>
                  <input
                    type="number"
                    value={formData.startSequence}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        startSequence: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Ej: 1, 100, -1"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.startSequence >= 1
                      ? `Las lecturas irán desde ${formData.startSequence} hasta ${formData.startSequence + formData.numberOfLectures - 1}`
                      : `⚠️ Secuencia negativa (${formData.startSequence}) - Para testing de anomalías`}
                  </p>
                </div>
              </div>
            </div>

            {/* Testing de Anomalías de Secuencia - Debajo de secuencia */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-red-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                🧪 Testing de anomalías de secuencia
              </h3>
              <div className="space-y-4">
                {/* Toggle para anomalías de secuencia */}
                <div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="generateAnomalies"
                      checked={formData.generateAnomalies}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          generateAnomalies: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-red-600 focus:ring-red-500 accent-red-500"
                    />
                    <label htmlFor="generateAnomalies" className="text-sm text-red-800 font-medium">
                      🚫 Habilitar testing de anomalías de secuencia
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-red-600">
                    ⚠️ Esto generará gaps, secuencias negativas y duplicados para probar el sistema
                    de alertas
                  </p>
                </div>

                {/* Configuración de anomalías */}
                {formData.generateAnomalies && (
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Configuración de anomalías
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Probabilidad de gaps (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={Math.round(formData.anomalyConfig.gapProbability * 100)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              anomalyConfig: {
                                ...prev.anomalyConfig,
                                gapProbability: (parseInt(e.target.value) || 0) / 100,
                              },
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Probabilidad de negativos (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={Math.round(formData.anomalyConfig.negativeProbability * 100)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              anomalyConfig: {
                                ...prev.anomalyConfig,
                                negativeProbability: (parseInt(e.target.value) || 0) / 100,
                              },
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Probabilidad de duplicados (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={Math.round(formData.anomalyConfig.duplicateProbability * 100)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              anomalyConfig: {
                                ...prev.anomalyConfig,
                                duplicateProbability: (parseInt(e.target.value) || 0) / 100,
                              },
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Tamaño máximo de gap
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="1000"
                          value={formData.anomalyConfig.maxGapSize}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              anomalyConfig: {
                                ...prev.anomalyConfig,
                                maxGapSize: parseInt(e.target.value) || 50,
                              },
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-red-100 rounded border border-red-200">
                      <p className="text-xs text-red-800">
                        <strong>Ejemplo:</strong> Con 10% gaps, 5% negativos, 2% duplicados y gap
                        máximo 50, en 100 lecturas esperarías ~10 gaps, ~5 negativos y ~2 duplicados
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ZONA 2: Tipos de Evento */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                Zona 2: Tipos de evento
              </h3>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Tipos de evento</label>
                <button
                  type="button"
                  onClick={toggleAllEventTypes}
                  className={`px-3 py-1 text-xs rounded-full border transition-all duration-200 ${
                    eventTypeOptions.every((type) => formData.selectedTypes.includes(type.value))
                      ? 'bg-gray-600 border-gray-700 text-white hover:bg-gray-700'
                      : 'bg-orange-100 border-orange-300 text-orange-800 hover:bg-orange-200'
                  }`}
                  disabled={loadingEventTypes || eventTypeOptions.length === 0}
                >
                  {eventTypeOptions.every((type) => formData.selectedTypes.includes(type.value))
                    ? 'Deseleccionar todo'
                    : 'Seleccionar todo'}
                </button>
              </div>
              {loadingEventTypes && (
                <p className="mb-2 text-xs text-gray-500">Cargando tipos de evento desde BD...</p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {eventTypeOptions.map((option) => {
                  const isSelected = formData.selectedTypes.includes(option.value);
                  const isLocked = LOCKED_EVENT_CODES.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center space-x-2 p-2 rounded border transition-all duration-200 ${
                        isLocked ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                      } ${isSelected ? 'shadow-sm' : 'hover:shadow-sm'}`}
                      style={{
                        backgroundColor: isSelected ? option.styles.selectedBg : option.styles.bg,
                        borderColor: isSelected
                          ? option.styles.selectedBorder
                          : option.styles.border,
                        color: isSelected ? option.styles.selectedText : option.styles.text,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isLocked}
                        onChange={() => toggleEventType(option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span
                        className={`flex items-center gap-1 text-sm ${isSelected ? 'font-semibold' : 'font-normal'}`}
                      >
                        {option.icon}
                        {option.label}
                        {isLocked && <span className="text-xs text-gray-500">(fijo)</span>}
                        {!option.active && (
                          <span className="text-xs text-gray-500">(inactivo)</span>
                        )}
                        {option.custom && (
                          <span className="text-xs text-amber-700">(test)</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="mt-4 p-3 rounded border border-dashed border-amber-300 bg-amber-50">
                <p className="text-xs text-amber-900 mb-2">
                  Testing: agregá un código de 3 caracteres que <strong>no exista</strong> en el
                  catálogo. Al enviar, el backend lo auto-creará e integrará la lectura.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    maxLength={3}
                    value={customEventCodeInput}
                    onChange={(e) =>
                      setCustomEventCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                    }
                    placeholder="XYZ"
                    className="w-24 border border-amber-300 rounded px-2 py-1 text-sm font-mono uppercase"
                  />
                  <button
                    type="button"
                    onClick={addCustomEventCode}
                    className="px-3 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700"
                  >
                    Agregar tipo inventado
                  </button>
                  {customEventCodes.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => removeCustomEventCode(code)}
                      className="px-2 py-1 text-xs rounded border border-amber-400 bg-white text-amber-900 font-mono"
                      title="Quitar"
                    >
                      {code} ×
                    </button>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Subiendo y bajando siempre están seleccionados
              </p>
            </div>

            {/* ZONA 3: Tiempo */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-900 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Zona 3: Tiempo
              </h3>
              <div className="space-y-4">
                {/* Date and Time - Layout mejorado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha y hora base
                  </label>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <DatePicker
                        selected={selectedDate}
                        onChange={(date) => handleDateChange(date as Date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={1}
                        dateFormat="yyyy/MM/dd HH:mm"
                        locale="es"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        calendarClassName="react-datepicker-custom"
                        timeClassName={() => 'react-datepicker-time-custom'}
                        popperClassName="react-datepicker-popper-custom"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={setToday}
                        className="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
                      >
                        Hoy
                      </button>
                      <button
                        type="button"
                        onClick={setNextDay}
                        className="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
                      >
                        Siguiente día
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustTime(1)}
                        className="px-3 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 text-sm"
                      >
                        +1min
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustTime(5)}
                        className="px-3 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 text-sm"
                      >
                        +5min
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustTime(10)}
                        className="px-3 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 text-sm"
                      >
                        +10min
                      </button>
                      <button
                        type="button"
                        onClick={() => adjustTime(30)}
                        className="px-3 py-2 bg-gray-200 text-black rounded hover:bg-gray-300 text-sm"
                      >
                        +30min
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Fecha y hora de inicio para las lecturas
                  </p>

                  {/* Zona horaria: la fecha/hora se interpreta en esta zona antes de enviar al backend */}
                  <div className="mt-4">
                    <label
                      htmlFor="bulk-tz"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Zona horaria
                    </label>
                    <select
                      id="bulk-tz"
                      value={formData.tz ?? TIMEZONE_OPTIONS[0].value}
                      onChange={(e) => setFormData((prev) => ({ ...prev, tz: e.target.value }))}
                      className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                    >
                      {TIMEZONE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      La fecha y hora de arriba se interpretan en esta zona al guardar
                    </p>
                  </div>
                </div>

                {/* Time Spacing Configuration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Espaciado entre lecturas
                  </label>
                  <div className="flex gap-4 items-center">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="spacing-seconds"
                        name="timeSpacing"
                        checked={formData.timeSpacing.type === 'seconds'}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            timeSpacing: { ...prev.timeSpacing, type: 'seconds' },
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="spacing-seconds" className="text-sm text-black">
                        Segundos
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="spacing-minutes"
                        name="timeSpacing"
                        checked={formData.timeSpacing.type === 'minutes'}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            timeSpacing: { ...prev.timeSpacing, type: 'minutes' },
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="spacing-minutes" className="text-sm text-black">
                        Minutos
                      </label>
                    </div>
                    <input
                      type="number"
                      min="1"
                      max={formData.timeSpacing.type === 'minutes' ? 30 : 3600}
                      value={formData.timeSpacing.value}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          timeSpacing: {
                            ...prev.timeSpacing,
                            value: parseInt(e.target.value) || 1,
                          },
                        }))
                      }
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-sm text-gray-600">
                      {formData.timeSpacing.type === 'minutes' ? 'min' : 'seg'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Tiempo entre cada lectura generada</p>

                  {/* Dynamic calculation of lecture dates */}
                  {(() => {
                    const dates = calculateLectureDates();
                    return (
                      <div className="mt-2 p-2 bg-orange-100 rounded border border-orange-200">
                        <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
                          <span className="text-xs text-orange-800 font-semibold">
                            Fecha lectura inicial:
                          </span>
                          <span className="text-xs text-orange-800">
                            {dates.initial.toLocaleString('es-ES')}
                          </span>
                          <span className="text-xs text-orange-800 font-semibold">
                            Fecha lectura final:
                          </span>
                          <span className="text-xs text-orange-800">
                            {dates.final.toLocaleString('es-ES')}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {formData.timeSpacing.type === 'minutes' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Máximo 30 minutos para mantener una sola operación
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Testing de Anomalías de Fecha - Debajo de tiempo */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                🧪 Testing de anomalías de fecha
              </h3>
              <div className="space-y-4">
                {/* Toggle para anomalías de fecha */}
                <div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="enableDateAnomalies"
                      checked={formData.enableDateAnomalies}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          enableDateAnomalies: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 accent-yellow-500"
                    />
                    <label
                      htmlFor="enableDateAnomalies"
                      className="text-sm text-yellow-800 font-medium"
                    >
                      🚫 Habilitar testing de anomalías de fecha
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ Esto generará fechas en el futuro (2400) o pasado (1950) para probar el
                    sistema de alertas
                  </p>
                </div>

                {/* Configuración de anomalías de fecha */}
                {formData.enableDateAnomalies && (
                  <div className="bg-white rounded-lg p-4 border border-yellow-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Configuración de anomalías de fecha
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Probabilidad de fecha en el futuro (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={Math.round((formData.futureDateProbability || 0) * 100)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              futureDateProbability: (parseInt(e.target.value) || 0) / 100,
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Probabilidad de fecha en futuro ( mayor a 2400)
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Probabilidad de fecha en el pasado (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={Math.round((formData.pastDateProbability || 0) * 100)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              pastDateProbability: (parseInt(e.target.value) || 0) / 100,
                            }))
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Probabilidad de fecha en 1950 (año pasado)
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-yellow-100 rounded border border-yellow-200">
                      <p className="text-xs text-yellow-800">
                        <strong>Ejemplo:</strong> Con 10% futuro y 5% pasado, en 100 lecturas
                        esperarías ~10 fechas en futuro ( mayor a 2400) y ~5 fechas en pasado (
                        menor a 1960).
                        <br />
                        <strong>⚠️ Útil para testing:</strong> Las fechas anteriores activarán la
                        detección de &quot;fechas anteriores&quot; en el sistema de alertas.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ZONA 4: Ángulos y Límites */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                Zona 4: Ángulos y límites
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ángulo lateral (min-max)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={formData.angleRanges.lat.min}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          angleRanges: {
                            ...prev.angleRanges,
                            lat: {
                              ...prev.angleRanges.lat,
                              min: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                            },
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={formData.angleRanges.lat.max}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          angleRanges: {
                            ...prev.angleRanges,
                            lat: {
                              ...prev.angleRanges.lat,
                              max: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                            },
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Max"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Rango de ángulos laterales en grados</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ángulo longitudinal (min-max)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={formData.angleRanges.long.min}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          angleRanges: {
                            ...prev.angleRanges,
                            long: {
                              ...prev.angleRanges.long,
                              min: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                            },
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      step="0.1"
                      value={formData.angleRanges.long.max}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          angleRanges: {
                            ...prev.angleRanges,
                            long: {
                              ...prev.angleRanges.long,
                              max: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                            },
                          },
                        }))
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Max"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Rango de ángulos longitudinales en grados
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Límites lateral (aplicado como ±valor)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.limitValues.lat}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        limitValues: {
                          ...prev.limitValues,
                          lat: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                        },
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    placeholder="Ej: 5"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Valor fijo que se aplicará como -{formData.limitValues.lat} y +
                    {formData.limitValues.lat}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Límites longitudinal (aplicado como ±valor)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.limitValues.long}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        limitValues: {
                          ...prev.limitValues,
                          long: Math.round((parseFloat(e.target.value) || 0) * 10) / 10,
                        },
                      }))
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                    placeholder="Ej: 5"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Valor fijo que se aplicará como -{formData.limitValues.long} y +
                    {formData.limitValues.long}
                  </p>
                </div>
              </div>

              {/* Ejemplo con grilla como otros controles */}
              <div className="mt-3 p-2 bg-orange-100 rounded border border-orange-200">
                <div className="text-xs text-orange-800">
                  <strong>Ejemplo:</strong> Si pones 3 en lateral y 4 en longitudinal, los límites
                  serán:
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 mt-1">
                    <span className="font-semibold">Lateral:</span>
                    <span>
                      -{formData.limitValues.lat} y +{formData.limitValues.lat}
                    </span>
                    <span className="font-semibold">Longitudinal:</span>
                    <span>
                      -{formData.limitValues.long} y +{formData.limitValues.long}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ZONA 5: Ubicación */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-900 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Zona 5: Ubicación
              </h3>

              {/* City Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar ciudad
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {Object.entries(CITY_COORDINATES).map(([key, city]) => (
                    <label
                      key={key}
                      className={`flex items-center space-x-2 cursor-pointer p-2 rounded border transition-all duration-200 ${
                        formData.selectedCity === key
                          ? 'bg-orange-100 border-orange-300 text-orange-800 shadow-sm'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:shadow-sm'
                      }`}
                    >
                      <input
                        type="radio"
                        name="city"
                        value={key}
                        checked={formData.selectedCity === key}
                        onChange={() => handleCityChange(key)}
                        className="text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm">{city.name}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Selecciona una ciudad para centrar el mapa
                </p>
              </div>

              <div id="map-container" className="w-full h-[400px] rounded-lg"></div>
              <div className="mt-4 text-sm text-gray-600">
                <p>
                  Centroide: {formData.centerCoords.lat.toFixed(6)},{' '}
                  {formData.centerCoords.long.toFixed(6)}
                </p>
                <p>Variación: ±{formData.variationMeters}m</p>
              </div>

              {/* Variation in meters - Included in location zone */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Variación en metros (±)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.variationMeters}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      variationMeters: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Aleatorizará +/- la cantidad indicada en metros entre cada lectura
                </p>
              </div>
            </div>

            {/* Delay - Hidden but maintained */}
            <input
              type="hidden"
              value={formData.delayMs}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  delayMs: parseInt(e.target.value) || 0,
                }))
              }
            />

            {/* Auto Increment Sequence */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-white mb-3">⚙️ Ajustes post-ejecución</h3>

              <div className="space-y-4">
                {/* Auto incrementar de secuencia */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">
                    Auto incrementar de secuencia
                  </h4>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoIncrementSequence"
                      checked={formData.autoIncrementSequence}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          autoIncrementSequence: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 accent-orange-500"
                    />
                    <label htmlFor="autoIncrementSequence" className="text-sm text-gray-300">
                      {formData.autoIncrementSequence
                        ? 'Auto-incrementar activado'
                        : 'Auto-incrementar desactivado'}
                    </label>
                  </div>
                </div>

                {/* Auto incremento de hora */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">
                    Auto incremento de hora
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="keep_same"
                        name="timeAdjustmentMode"
                        value="keep_same"
                        checked={formData.timeAdjustmentMode === 'keep_same'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            timeAdjustmentMode: e.target.value as
                              | 'keep_same'
                              | 'move_to_end'
                              | 'move_to_end_plus_31',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="keep_same" className="text-sm text-gray-300">
                        Sin incremento
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="move_to_end"
                        name="timeAdjustmentMode"
                        value="move_to_end"
                        checked={formData.timeAdjustmentMode === 'move_to_end'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            timeAdjustmentMode: e.target.value as
                              | 'keep_same'
                              | 'move_to_end'
                              | 'move_to_end_plus_31',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="move_to_end" className="text-sm text-gray-300">
                        Mover hacia el final (ej: 10 lecturas de 1min = +9min)
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="move_to_end_plus_31"
                        name="timeAdjustmentMode"
                        value="move_to_end_plus_31"
                        checked={formData.timeAdjustmentMode === 'move_to_end_plus_31'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            timeAdjustmentMode: e.target.value as
                              | 'keep_same'
                              | 'move_to_end'
                              | 'move_to_end_plus_31',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="move_to_end_plus_31" className="text-sm text-gray-300">
                        Mover para generar una nueva operación (ej: 10 lecturas de 1min = +40min)
                      </label>
                    </div>
                  </div>
                </div>

                {/* Variación de posición post-ejecución */}
                <div>
                  <h4 className="text-sm font-medium text-gray-300 mb-2">
                    Variar posición post-ejecución
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="post_position_no"
                        name="postPositionVariationMode"
                        value="no"
                        checked={formData.positionVariationMode === 'no'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            positionVariationMode: e.target.value as
                              | 'no'
                              | 'vary_10km'
                              | 'vary_cities',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="post_position_no" className="text-sm text-gray-300">
                        No variar: Posición fija
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="post_position_vary_10km"
                        name="postPositionVariationMode"
                        value="vary_10km"
                        checked={formData.positionVariationMode === 'vary_10km'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            positionVariationMode: e.target.value as
                              | 'no'
                              | 'vary_10km'
                              | 'vary_cities',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="post_position_vary_10km" className="text-sm text-gray-300">
                        Variar +/- 10 km: Con protección contra caer en el mar
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="post_position_vary_cities"
                        name="postPositionVariationMode"
                        value="vary_cities"
                        checked={formData.positionVariationMode === 'vary_cities'}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            positionVariationMode: e.target.value as
                              | 'no'
                              | 'vary_10km'
                              | 'vary_cities',
                          }))
                        }
                        className="text-orange-600 focus:ring-orange-500 accent-orange-500"
                      />
                      <label htmlFor="post_position_vary_cities" className="text-sm text-gray-300">
                        Variar entre ciudades: Selecciona aleatoriamente entre las ciudades
                        disponibles
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Indicador del modo actual */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-orange-900 mb-3">
                📊 Estado actual de ajustes
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Secuencia:</span>
                    {formData.autoIncrementSequence ? (
                      <span className="text-green-600 text-sm font-medium">✅ Activado</span>
                    ) : (
                      <span className="text-red-600 text-sm font-medium">❌ Desactivado</span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Hora:</span>
                    {formData.timeAdjustmentMode === 'keep_same' && (
                      <span className="text-gray-600 text-sm font-medium">🕐 Sin incremento</span>
                    )}
                    {formData.timeAdjustmentMode === 'move_to_end' && (
                      <span className="text-orange-600 text-sm font-medium">⏭️ Al final</span>
                    )}
                    {formData.timeAdjustmentMode === 'move_to_end_plus_31' && (
                      <span className="text-orange-700 text-sm font-medium">⏭️+31 Nueva op.</span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Posición:</span>
                    {formData.positionVariationMode === 'no' ? (
                      <span className="text-gray-600 text-sm font-medium">📍 Fija</span>
                    ) : formData.positionVariationMode === 'vary_10km' ? (
                      <span className="text-blue-600 text-sm font-medium">🌊 +/- 10km</span>
                    ) : (
                      <span className="text-purple-600 text-sm font-medium">🏙️ Entre ciudades</span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Secuencia:</span>
                    {formData.generateAnomalies ? (
                      <span className="text-red-600 text-sm font-medium">🧪 Anomalías activas</span>
                    ) : (
                      <span className="text-gray-600 text-sm font-medium">✅ Normal</span>
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Fechas:</span>
                    {formData.enableDateAnomalies ? (
                      <span className="text-yellow-600 text-sm font-medium">
                        🕐 Anomalías{' '}
                        {formData.futureDateProbability > 0 && formData.pastDateProbability > 0
                          ? 'futuro + pasado'
                          : formData.pastDateProbability > 0
                            ? 'pasado'
                            : 'futuro'}{' '}
                        {Math.round(
                          (formData.futureDateProbability + formData.pastDateProbability) * 100
                        )}
                        %
                      </span>
                    ) : (
                      <span className="text-gray-600 text-sm font-medium">✅ Sin anomalías</span>
                    )}
                  </div>
                </div>
              </div>
              {formData.timeAdjustmentMode !== 'keep_same' && (
                <div className="mt-3 bg-orange-100 rounded-lg p-3 border border-orange-300">
                  <p className="text-xs text-orange-800">
                    <strong>Ejemplo:</strong> Si envías 10 lecturas con espaciado de 1 minuto, la
                    hora se moverá{' '}
                    {formData.timeAdjustmentMode === 'move_to_end' ? '9 minutos' : '40 minutos'}{' '}
                    hacia adelante
                  </p>
                </div>
              )}
            </div>

            {/* Información sobre los métodos - OCULTO */}
            {/* <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <h3 className="text-sm font-semibold text-orange-900 mb-2">📋 Métodos de Inserción</h3>
              <div className="text-xs text-orange-800 space-y-1">
                <p>
                  <strong>📤 Método Tradicional:</strong> Envía cada lectura individualmente al
                  backend
                </p>
                <p>
                  <strong>🚀 Nuevo Método Batch:</strong> Envía todas las lecturas en un solo lote
                  al queue service
                </p>
                <p>
                  <strong>⚡ Batch con Colas:</strong> Envía lotes al nuevo sistema de colas por
                  dispositivo (procesamiento secuencial)
                </p>
                <p className="text-orange-600 mt-2">
                  💡 Usa los tres métodos para comparar rendimiento y funcionalidad
                </p>
              </div>
            </div> */}

            <div className="space-y-3">
              {/* Botón normal - habilitado solo si NO hay anomalías habilitadas */}
              <button
                type="button"
                onClick={handleGeneratePreview}
                disabled={isLoading || formData.generateAnomalies || formData.enableDateAnomalies}
                className={`px-4 py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 w-full ${
                  isLoading || formData.generateAnomalies || formData.enableDateAnomalies
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-brand-orange text-brand-white hover:bg-opacity-90'
                }`}
              >
                <Package className="w-4 h-4" />
                Generar {formData.numberOfLectures} lecturas como &quot;Vista previa&quot;
              </button>

              {/* Botón para generar con anomalías - habilitado solo si SÍ hay anomalías habilitadas */}
              <button
                type="button"
                onClick={handleGeneratePreview}
                disabled={
                  isLoading || (!formData.generateAnomalies && !formData.enableDateAnomalies)
                }
                className={`px-4 py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 w-full ${
                  isLoading || (!formData.generateAnomalies && !formData.enableDateAnomalies)
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <AlertTriangle className="w-4 h-4" />
                🧪 Generar {formData.numberOfLectures} lecturas CON ANOMALÍAS para testing
                {formData.generateAnomalies &&
                  formData.enableDateAnomalies &&
                  ' (secuencia + fecha)'}
                {formData.generateAnomalies && !formData.enableDateAnomalies && ' (secuencia)'}
                {!formData.generateAnomalies && formData.enableDateAnomalies && ' (fecha)'}
              </button>

              {/* Botón para limpiar anomalías */}
              {(formData.generateAnomalies || formData.enableDateAnomalies) && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      generateAnomalies: false,
                      enableDateAnomalies: false,
                      futureDateProbability: 0,
                      pastDateProbability: 0,
                    }));
                    setPreviewLectures([]);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center gap-2 w-full"
                >
                  <Package className="w-4 h-4" />
                  Limpiar anomalías y vista previa
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center gap-2">
            <Braces className="w-8 h-8 text-brand-orange" />
            Vista previa de lecturas generadas
          </h2>
          {previewLectures.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-2 text-sm rounded-full bg-orange-100 text-orange-800">
                {previewLectures.length} lecturas generadas
              </span>
              <button
                onClick={() => setPreviewLectures([])}
                className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                {formData.generateAnomalies
                  ? 'Limpiar vista previa con anomalías'
                  : 'Limpiar vista previa normal'}
              </button>
            </div>
          )}
        </div>

        {previewLectures.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Vista resumida */}
            <div className="bg-gray-100 p-4 rounded max-h-96 overflow-auto">
              <h3 className="text-sm font-semibold text-brand-black mb-3">
                Resumen de lecturas
                {formData.generateAnomalies && (
                  <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                    🧪 CON ANOMALÍAS
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                {previewLectures.map((lecture, index) => {
                  const eventType = eventTypesMap.get(lecture.type as string);

                  // Detectar anomalías de secuencia
                  const isSequenceAnomaly =
                    formData.generateAnomalies &&
                    (lecture.seq < 0 ||
                      (index > 0 && lecture.seq === previewLectures[index - 1].seq) ||
                      (index > 0 && lecture.seq !== previewLectures[index - 1].seq + 1));

                  // Detectar anomalías de fecha
                  const lectureDate = new Date(parseInt(lecture.ts) * 1000);
                  const isDateAnomaly =
                    formData.enableDateAnomalies &&
                    (lectureDate.getFullYear() <= 1960 || // Fecha en el pasado
                      lectureDate.getFullYear() >= 2300); // Fecha en el futuro

                  const isAnomaly = isSequenceAnomaly || isDateAnomaly;
                  const hasBothAnomalies = isSequenceAnomaly && isDateAnomaly;

                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-2 rounded text-xs ${
                        isAnomaly
                          ? hasBothAnomalies
                            ? 'bg-purple-100 border border-purple-300'
                            : isDateAnomaly
                              ? 'bg-yellow-100 border border-yellow-300'
                              : 'bg-red-100 border border-red-300'
                          : 'bg-gray-100 border border-gray-200'
                      } ${isAnomaly ? (hasBothAnomalies ? 'text-purple-800' : isDateAnomaly ? 'text-yellow-800' : 'text-red-800') : ''}`}
                      style={
                        !isAnomaly
                          ? {
                              backgroundColor: eventType?.styles.bg || '#f3f4f6',
                              borderColor: eventType?.styles.border || '#e5e7eb',
                              color: eventType?.styles.text || '#1f2937',
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">#{index + 1}</span>
                        {isAnomaly && (
                          <span
                            className={
                              hasBothAnomalies
                                ? 'text-purple-600'
                                : isDateAnomaly
                                  ? 'text-yellow-600'
                                  : 'text-red-600'
                            }
                          >
                            {hasBothAnomalies ? '🚨' : isDateAnomaly ? '🕐' : '⚠️'}
                          </span>
                        )}
                        <span className="font-mono text-xs">seq: {lecture.seq}</span>
                        {isDateAnomaly && (
                          <span className="text-xs text-yellow-600">
                            {lectureDate.getFullYear() <= 1960 ? '1950' : '2500'}
                          </span>
                        )}
                        {hasBothAnomalies && (
                          <span className="text-xs text-purple-600 font-bold">
                            SECUENCIA + FECHA
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {eventType?.icon}
                        <span className="font-semibold">{lecture.type}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Análisis de anomalías */}
              {(formData.generateAnomalies || formData.enableDateAnomalies) && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h4 className="text-xs font-semibold text-red-800 mb-2">
                    Análisis de anomalías detectadas:
                  </h4>
                  {(() => {
                    const sequences = previewLectures.map((l) => l.seq);
                    const negatives = sequences.filter((s) => s < 0);
                    const duplicates = sequences.filter((s, i) => sequences.indexOf(s) !== i);
                    const gaps = sequences.filter((s, i) => i > 0 && s !== sequences[i - 1] + 1);

                    // Análisis de anomalías de fecha
                    const futureDates = previewLectures.filter((l) => {
                      const lectureDate = new Date(parseInt(l.ts) * 1000);
                      return lectureDate.getFullYear() >= 2300;
                    });
                    const pastDates = previewLectures.filter((l) => {
                      const lectureDate = new Date(parseInt(l.ts) * 1000);
                      return lectureDate.getFullYear() <= 1960;
                    });

                    // Análisis de anomalías combinadas
                    const bothAnomalies = previewLectures.filter((l, index) => {
                      const lectureDate = new Date(parseInt(l.ts) * 1000);
                      const isSequenceAnomaly =
                        formData.generateAnomalies &&
                        (l.seq < 0 ||
                          (index > 0 && l.seq === previewLectures[index - 1].seq) ||
                          (index > 0 && l.seq !== previewLectures[index - 1].seq + 1));
                      const isDateAnomaly =
                        formData.enableDateAnomalies &&
                        (lectureDate.getFullYear() <= 1960 || lectureDate.getFullYear() >= 2300);
                      return isSequenceAnomaly && isDateAnomaly;
                    });

                    return (
                      <div className="text-xs text-red-700 space-y-1">
                        {negatives.length > 0 && (
                          <div>🚨 Secuencias negativas: {negatives.join(', ')}</div>
                        )}
                        {duplicates.length > 0 && (
                          <div>🔄 Duplicados detectados: {duplicates.length}</div>
                        )}
                        {gaps.length > 0 && <div>📊 Gaps detectados: {gaps.length}</div>}
                        {futureDates.length > 0 && (
                          <div>🚀 Fechas en futuro ( mayor a 2400): {futureDates.length}</div>
                        )}
                        {pastDates.length > 0 && (
                          <div>📅 Fechas en pasado ( menor a 1960): {pastDates.length}</div>
                        )}
                        {bothAnomalies.length > 0 && (
                          <div className="font-bold text-purple-700">
                            🚨 Anomalías combinadas (secuencia + fecha): {bothAnomalies.length}
                          </div>
                        )}
                        {negatives.length === 0 &&
                          duplicates.length === 0 &&
                          gaps.length === 0 &&
                          futureDates.length === 0 &&
                          pastDates.length === 0 && (
                            <div>✅ No se detectaron anomalías en esta generación</div>
                          )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* JSON completo */}
            <div className="lg:col-span-2 bg-gray-100 p-4 rounded max-h-96 overflow-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">JSON Completo</h3>
                <button
                  type="button"
                  onClick={handleDownloadPreviewJson}
                  className="px-3 py-1 text-xs bg-orange-100 text-orange-800 rounded hover:bg-orange-200 transition-colors flex items-center gap-1"
                >
                  <Download className="w-3 h-3" />
                  Bajar JSON para importar
                </button>
              </div>
              <pre className="text-sm">{JSON.stringify(previewLectures, null, 2)}</pre>
            </div>
          </div>
        ) : (
          <div className="bg-gray-100 p-8 rounded text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No hay vista previa generada</p>
            <p className="text-sm">
              Configura los parámetros y haz clic en &quot;Generar vista previa&quot; para ver las
              lecturas que se crearán
            </p>
          </div>
        )}

        {/* Botón de envío movido aquí - debajo de la vista previa */}
        {previewLectures.length > 0 && (
          <div className="mt-6">
            <button
              type="button"
              onClick={handleSendBatchWithQueue}
              disabled={isLoading}
              className={`px-4 py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 w-full ${
                isLoading
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Procesando...
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4" />
                  {`Enviar ${previewLectures.length} lecturas como un lote${formData.generateAnomalies ? ' con anomalías' : ''}`}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Results Section */}
      {insertResults.length > 0 && (
        <div className="mt-8 bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span>Resultados de inserción</span>
              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                {insertResults.filter((r) => r.success).length} exitosas / {insertResults.length}{' '}
                total
              </span>
            </h2>
            <button
              onClick={() => setInsertResults([])}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Limpiar
            </button>
          </div>

          <div className="max-h-96 overflow-auto">
            <div className="space-y-2">
              {insertResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-3 rounded border-l-4 ${
                    result.success
                      ? 'bg-green-50 border-green-400 text-green-800'
                      : 'bg-red-50 border-red-400 text-red-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {result.data && typeof result.data === 'object' && 'batchId' in result.data
                        ? `Lote ${result.index}: ${result.success ? '✅ Éxito' : '❌ Error'}`
                        : `Lectura ${result.index}: ${result.success ? '✅ Éxito' : '❌ Error'}`}
                    </span>
                    {result.success && (
                      <span className="text-xs text-green-600">
                        {result.data && typeof result.data === 'object' && 'batchId' in result.data
                          ? `Batch ID: ${(result.data as { batchId: string }).batchId}`
                          : `ID: ${(result.data as { id?: string })?.id || 'N/A'}`}
                      </span>
                    )}
                  </div>
                  {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                  {result.success && result.data && (result.data as any).batchId && (
                    <div className="mt-1 text-xs text-green-600">
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {(result.data as any).message} - {(result.data as any).count} lecturas
                      procesadas
                      {(result.data as { estimatedProcessingTime?: number })
                        .estimatedProcessingTime && (
                        <div className="mt-1">
                          Tiempo estimado:{' '}
                          {Math.round(
                            (result.data as { estimatedProcessingTime: number })
                              .estimatedProcessingTime / 1000
                          )}
                          s
                        </div>
                      )}
                    </div>
                  )}
                  {!result.success && (
                    <div className="mt-1 text-xs">
                      Error:{' '}
                      {String(
                        (result.error as { message?: string })?.message || 'Error desconocido'
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sección de última inserción - copia de la UI de vista previa */}
          {lastInsertedLectures.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Braces className="w-6 h-6 text-brand-orange" />
                Última inserción realizada
                {lastInsertedHadAnomalies && (
                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                    🧪 CON ANOMALÍAS
                  </span>
                )}
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Vista resumida */}
                <div className="bg-gray-100 p-4 rounded max-h-96 overflow-auto">
                  <h4 className="text-sm font-semibold text-brand-black mb-3">
                    Resumen de lecturas insertadas
                    {lastInsertedHadAnomalies && (
                      <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                        🧪 CON ANOMALÍAS
                      </span>
                    )}
                  </h4>
                  <div className="space-y-2">
                    {lastInsertedLectures.map((lecture, index) => {
                      const eventType = eventTypesMap.get(lecture.type as string);

                      // Detectar anomalías de secuencia
                      const isSequenceAnomaly =
                        lastInsertedHadAnomalies &&
                        (lecture.seq < 0 ||
                          (index > 0 && lecture.seq === lastInsertedLectures[index - 1].seq) ||
                          (index > 0 && lecture.seq !== lastInsertedLectures[index - 1].seq + 1));

                      // Detectar anomalías de fecha
                      const lectureDate = new Date(parseInt(lecture.ts) * 1000);
                      const isDateAnomaly =
                        lectureDate.getFullYear() <= 1960 || // Fecha en el pasado
                        lectureDate.getFullYear() >= 2300; // Fecha en el futuro

                      const isAnomaly = isSequenceAnomaly || isDateAnomaly;
                      const hasBothAnomalies = isSequenceAnomaly && isDateAnomaly;

                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-2 rounded text-xs ${
                            isAnomaly
                              ? hasBothAnomalies
                                ? 'bg-purple-100 border border-purple-300'
                                : isDateAnomaly
                                  ? 'bg-yellow-100 border border-yellow-300'
                                  : 'bg-red-100 border border-red-300'
                              : 'bg-gray-100 border border-gray-200'
                          } ${isAnomaly ? (hasBothAnomalies ? 'text-purple-800' : isDateAnomaly ? 'text-yellow-800' : 'text-red-800') : ''}`}
                          style={
                            !isAnomaly
                              ? {
                                  backgroundColor: eventType?.styles.bg || '#f3f4f6',
                                  borderColor: eventType?.styles.border || '#e5e7eb',
                                  color: eventType?.styles.text || '#1f2937',
                                }
                              : undefined
                          }
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">#{index + 1}</span>
                            {isAnomaly && (
                              <span
                                className={
                                  hasBothAnomalies
                                    ? 'text-purple-600'
                                    : isDateAnomaly
                                      ? 'text-yellow-600'
                                      : 'text-red-600'
                                }
                              >
                                {hasBothAnomalies ? '🚨' : isDateAnomaly ? '🕐' : '⚠️'}
                              </span>
                            )}
                            <span className="font-mono text-xs">seq: {lecture.seq}</span>
                            {isDateAnomaly && (
                              <span className="text-xs text-yellow-600">
                                {lectureDate.getFullYear() <= 1950 ? '1950' : '2500'}
                              </span>
                            )}
                            {hasBothAnomalies && (
                              <span className="text-xs text-purple-600 font-bold">
                                SECUENCIA + FECHA
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {eventType?.icon}
                            <span className="font-semibold">{lecture.type}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Análisis de anomalías */}
                  {lastInsertedHadAnomalies && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                      <h5 className="text-xs font-semibold text-red-800 mb-2">
                        Análisis de anomalías detectadas:
                      </h5>
                      {(() => {
                        const sequences = lastInsertedLectures.map((l) => l.seq);
                        const negatives = sequences.filter((s) => s < 0);
                        const duplicates = sequences.filter((s, i) => sequences.indexOf(s) !== i);
                        const gaps = sequences.filter(
                          (s, i) => i > 0 && s !== sequences[i - 1] + 1
                        );

                        // Análisis de anomalías de fecha
                        const futureDates = lastInsertedLectures.filter((l) => {
                          const lectureDate = new Date(parseInt(l.ts) * 1000);
                          return lectureDate.getFullYear() >= 2300;
                        });
                        const pastDates = lastInsertedLectures.filter((l) => {
                          const lectureDate = new Date(parseInt(l.ts) * 1000);
                          return lectureDate.getFullYear() <= 1960;
                        });

                        // Análisis de anomalías combinadas
                        const bothAnomalies = lastInsertedLectures.filter((l, index) => {
                          const lectureDate = new Date(parseInt(l.ts) * 1000);
                          const isSequenceAnomaly =
                            lastInsertedHadAnomalies &&
                            (l.seq < 0 ||
                              (index > 0 && l.seq === lastInsertedLectures[index - 1].seq) ||
                              (index > 0 && l.seq !== lastInsertedLectures[index - 1].seq + 1));
                          const isDateAnomaly =
                            lectureDate.getFullYear() <= 1960 || lectureDate.getFullYear() >= 2300;
                          return isSequenceAnomaly && isDateAnomaly;
                        });

                        return (
                          <div className="text-xs text-red-700 space-y-1">
                            {negatives.length > 0 && (
                              <div>🚨 Secuencias negativas: {negatives.join(', ')}</div>
                            )}
                            {duplicates.length > 0 && (
                              <div>🔄 Duplicados detectados: {duplicates.length}</div>
                            )}
                            {gaps.length > 0 && <div>📊 Gaps detectados: {gaps.length}</div>}
                            {futureDates.length > 0 && (
                              <div>🚀 Fechas en futuro ( mayor a 2400): {futureDates.length}</div>
                            )}
                            {pastDates.length > 0 && (
                              <div>📅 Fechas en pasado ( menor a 1960): {pastDates.length}</div>
                            )}
                            {bothAnomalies.length > 0 && (
                              <div className="font-bold text-purple-700">
                                🚨 Anomalías combinadas (secuencia + fecha): {bothAnomalies.length}
                              </div>
                            )}
                            {negatives.length === 0 &&
                              duplicates.length === 0 &&
                              gaps.length === 0 &&
                              futureDates.length === 0 &&
                              pastDates.length === 0 && (
                                <div>✅ No se detectaron anomalías en esta inserción</div>
                              )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* JSON completo */}
                <div className="lg:col-span-2 bg-gray-100 p-4 rounded max-h-96 overflow-auto">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    JSON Completo de la inserción
                  </h4>
                  <pre className="text-sm">{JSON.stringify(lastInsertedLectures, null, 2)}</pre>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Section */}
      {formError && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
          <h3 className="font-semibold text-red-900 mb-2">Error</h3>
          <p className="text-red-700">{formError}</p>
        </div>
      )}
    </div>
  );
};
